// src/pages/SignUp.jsx
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { normalizeKoreanPhone } from '../utils/phone'

// ✅ 카카오 주소검색 스크립트 로더
const loadDaumPostcode = (callback) => {
  const existing = document.getElementById('daum-postcode-script')
  if (!existing) {
    const script = document.createElement('script')
    script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'
    script.id = 'daum-postcode-script'
    script.onload = callback
    document.body.appendChild(script)
  } else {
    callback()
  }
}


export default function SignUp() {
  const navigate = useNavigate()

  const [step, setStep] = useState(1) // 1: 기본정보, 2: 약사인증/주소
  console.log('[SignUp] Component Rendered, step:', step)

  // Step 1: 기본 정보
  const [role, setRole] = useState('pharmacist') // 'pharmacist' | 'pharmacy'
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // 공통 핸드폰 / 인증 상태
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [verificationSent, setVerificationSent] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')

  // Step 2: 전문 정보 & 주소
  const [postcode, setPostcode] = useState('')
  const [address, setAddress] = useState('')
  const [detailAddress, setDetailAddress] = useState('')
  const [pharmacyName, setPharmacyName] = useState('')
  const [licenseNumber, setLicenseNumber] = useState('')
  const [licenseFile, setLicenseFile] = useState(null)

  // ✅ 좌표 상태 추가
  const [latitude, setLatitude] = useState(null)
  const [longitude, setLongitude] = useState(null)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [infoMessage, setInfoMessage] = useState('')

  // 버튼 비활성화 해제용 타이머 (예기치 못한 중단 대비)
  useEffect(() => {
    let timer;
    if (isSubmitting) {
      timer = setTimeout(() => {
        setIsSubmitting(false);
        console.log('[SignUp] Watchdog: isSubmitting reset to false due to timeout');
      }, 10000); // 10초 후 자동 해제
    }
    return () => clearTimeout(timer);
  }, [isSubmitting])

  // ✅ 카카오 주소검색 실행 및 좌표 변환
  const handleSearchAddress = () => {
    loadDaumPostcode(() => {
      new window.daum.Postcode({
        oncomplete: (data) => {
          let fullAddress = data.address
          let extra = ''

          if (data.addressType === 'R') {
            if (data.bname !== '') extra += data.bname
            if (data.buildingName !== '') extra += extra !== '' ? ', ' + data.buildingName : data.buildingName
            fullAddress += extra !== '' ? ` (${extra})` : ''
          }

          setPostcode(data.zonecode)
          setAddress(fullAddress)
          setDetailAddress('')

          // ✅ Geocoding: 주소 -> 좌표 변환
          if (window.kakao && window.kakao.maps && window.kakao.maps.services) {
            const geocoder = new window.kakao.maps.services.Geocoder()
            geocoder.addressSearch(data.address, (result, status) => {
              if (status === window.kakao.maps.services.Status.OK) {
                const lat = parseFloat(result[0].y)
                const lng = parseFloat(result[0].x)
                console.log('Geocoding success:', lat, lng)
                setLatitude(lat)
                setLongitude(lng)
              } else {
                console.warn('Geocoding failed status:', status)
                // 좌표 없는 경우 null 유지
                setLatitude(null)
                setLongitude(null)
              }
            })
          } else {
            console.warn('Kakao Maps SDK not loaded or key invalid')
          }
        },
      }).open()
    })
  }

  // ✅ 면허증 파일 선택
  const handleLicenseFileChange = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setLicenseFile(file)
  }

  const handleSendVerification = async () => {
    setErrorMessage('')
    setInfoMessage('')

    const phoneDigits = normalizeKoreanPhone(phone)
    if (!phoneDigits) {
      setErrorMessage('핸드폰 번호를 입력해 주세요.')
      return
    }

    setPhoneVerified(false)
    setVerificationCode('')
    setVerificationSent(false)

    try {
      const r = await fetch('/api/auth/sms/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneDigits }),
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) {
        throw new Error(data?.error || '인증번호 발송에 실패했습니다.')
      }

      setVerificationSent(true)
      setInfoMessage('인증번호를 발송했습니다. 문자로 받은 6자리 코드를 입력해 주세요.')
    } catch (e) {
      setErrorMessage(e?.message || '인증번호 발송 중 오류가 발생했습니다.')
    }
  }

  const handleVerifyCode = async () => {
    setErrorMessage('')
    setInfoMessage('')

    const phoneDigits = normalizeKoreanPhone(phone)
    const codeDigits = String(verificationCode || '').replace(/[^\d]/g, '')

    if (!phoneDigits) {
      setErrorMessage('핸드폰 번호를 입력해 주세요.')
      return
    }
    if (codeDigits.length !== 6) {
      setErrorMessage('인증번호 6자리를 정확히 입력해 주세요.')
      return
    }

    try {
      const r = await fetch('/api/auth/sms/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneDigits, code: codeDigits }),
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) {
        throw new Error(data?.error || '인증에 실패했습니다.')
      }

      setPhoneVerified(true)
      setInfoMessage('핸드폰 번호 인증이 완료되었습니다.')
    } catch (e) {
      setPhoneVerified(false)
      setErrorMessage(e?.message || '인증 중 오류가 발생했습니다.')
    }
  }

  // Step 1에서 다음 단계로 넘어갈 때 검증
  const handleNextStep = () => {
    setErrorMessage('')
    const phoneDigits = normalizeKoreanPhone(phone)

    if (!name) {
      setErrorMessage('이름을 입력해 주세요.')
      return
    }
    if (!phoneDigits) {
      setErrorMessage('핸드폰 번호를 입력해 주세요.')
      return
    }
    if (!phoneVerified) {
      setErrorMessage('핸드폰 번호 인증을 완료해 주세요.')
      return
    }

    if (!password || !confirmPassword) {
      setErrorMessage('비밀번호를 입력해 주세요.')
      return
    }
    if (password !== confirmPassword) {
      setErrorMessage('비밀번호가 일치하지 않습니다.')
      return
    }
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d!@#$%^&*]{8,}$/
    if (!passwordRegex.test(password)) {
      setErrorMessage('비밀번호는 영문과 숫자를 포함하여 8자 이상이어야 합니다.')
      return
    }

    setStep(2)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    console.log('[SignUp] handleSubmit called')
    setErrorMessage('')
    setInfoMessage('')

    if (role === 'pharmacy' && !pharmacyName) {
      setErrorMessage('약국명을 입력해 주세요.')
      return
    }
    if (!licenseNumber) {
      setErrorMessage('약사 면허번호를 입력해 주세요.')
      return
    }
    if (!licenseFile) {
      setErrorMessage('면허증 사본을 업로드해 주세요.')
      return
    }
    if (!address) {
      setErrorMessage('주소를 검색 후 입력해 주세요.')
      return
    }
    if (!detailAddress) {
      setErrorMessage('상세 주소를 입력해 주세요.')
      return
    }

    setIsSubmitting(true)
    const phoneDigits = normalizeKoreanPhone(phone)

    try {
      // ✅ 파일 Base64 변환 (압축 적용)
      let licenseFileBase64 = null
      let licenseFileName = null

      if (licenseFile) {
        const compressImage = (file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
              const img = new Image();
              img.src = event.target.result;
              img.onload = () => {
                const canvas = document.createElement('canvas');
                const MAX_WIDTH = 1024;
                const MAX_HEIGHT = 1024;
                let width = img.width;
                let height = img.height;

                if (width > height) {
                  if (width > MAX_WIDTH) {
                    height *= MAX_WIDTH / width;
                    width = MAX_WIDTH;
                  }
                } else {
                  if (height > MAX_HEIGHT) {
                    width *= MAX_HEIGHT / height;
                    height = MAX_HEIGHT;
                  }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                // JPEG, quality 0.7
                const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
                resolve(dataUrl);
              };
              img.onerror = (err) => reject(err);
            };
            reader.onerror = (err) => reject(err);
          });
        }

        try {
          // 원본 이름 유지하되 확장자는 .jpg로 간주될 수 있음 (Base64 헤더 따름)
          licenseFileBase64 = await compressImage(licenseFile)
          licenseFileName = licenseFile.name
        } catch (compressErr) {
          console.error('Compression failed, falling back to original', compressErr)
          // Fallback to original if compression fails (though risky for 413)
          const fileToBase64 = (file) => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader()
              reader.readAsDataURL(file)
              reader.onload = () => resolve(reader.result)
              reader.onerror = (error) => reject(error)
            })
          }
          licenseFileBase64 = await fileToBase64(licenseFile)
          licenseFileName = licenseFile.name
        }
      }

      const r = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role,
          name,

          password,
          phone: phoneDigits,
          postcode,
          address,
          detailAddress,
          pharmacyName: role === 'pharmacy' ? pharmacyName : null,
          licenseNumber,

          // ✅ 좌표 추가
          latitude,
          longitude,

          // ✅ 파일 데이터 추가
          licenseFileBase64,
          licenseFileName
        }),
      })

      const data = await r.json().catch(() => ({}))

      if (!r.ok || !data.ok) {
        const msg = data?.detail || data?.message || data?.error || '회원가입 중 오류가 발생했습니다.'
        throw new Error(msg)
      }

      alert('FLOATER 회원 가입 신청이 완료되었습니다.\n승인이 완료되면 가입 신청이 완료되며, 승인까지는 최대 12시간 소요될 수 있습니다.')
      navigate('/login')
    } catch (err) {
      console.error(err)
      setErrorMessage(err?.message || '회원가입 중 오류가 발생했습니다.')
    } finally {
      console.log('[SignUp] handleSubmit finished')
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6">
        <h1 className="text-2xl font-bold text-center mb-2">FLOATER 회원가입</h1>
        <p className="text-sm text-gray-500 text-center mb-6">
          {step === 1 ? '1단계: 기본 정보 입력' : '2단계: 자격 확인 및 주소'}
        </p>

        {/* 역할 선택 - 1단계에서만 보여줌 */}
        {step === 1 && (
          <div className="flex mb-6 rounded-full bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => {
                setRole('pharmacist')
                setErrorMessage('')
              }}
              className={`flex-1 py-2 text-sm font-semibold rounded-full transition ${role === 'pharmacist' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}
            >
              약사 회원가입
            </button>
            <button
              type="button"
              onClick={() => {
                setRole('pharmacy')
                setErrorMessage('')
              }}
              className={`flex-1 py-2 text-sm font-semibold rounded-full transition ${role === 'pharmacy' ? 'bg-blue-600 text-white' : 'text-gray-600'}`}
            >
              약국 회원가입
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* ================= STEP 1 ================= */}
          {step === 1 && (
            <>
              {/* 이름 */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  이름 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="이름을 입력해 주세요."
                  required
                />
              </div>

              {/* 핸드폰 번호 (아이디) */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  핸드폰 번호 (아이디) <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value)
                      setPhoneVerified(false)
                      setVerificationSent(false)
                      setVerificationCode('')
                      setErrorMessage('')
                      setInfoMessage('')
                    }}
                    placeholder="예: 010-0000-0000"
                    required
                  />
                  <button
                    type="button"
                    onClick={handleSendVerification}
                    disabled={isSubmitting || phoneVerified}
                    className={`px-3 py-2 text-sm font-semibold rounded-lg border ${phoneVerified
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-blue-600 border-blue-500 hover:bg-blue-50'
                      }`}
                  >
                    {phoneVerified ? '인증 완료' : '인증 요청'}
                  </button>
                </div>

                {verificationSent && !phoneVerified && (
                  <div className="mt-2 flex gap-2">
                    <input
                      type="text"
                      className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="인증번호 6자리"
                    />
                    <button
                      type="button"
                      onClick={handleVerifyCode}
                      disabled={isSubmitting}
                      className="px-3 py-2 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      인증 확인
                    </button>
                  </div>
                )}
                {phoneVerified && (
                  <p className="mt-1 text-xs text-green-600">핸드폰 번호 인증이 완료되었습니다.</p>
                )}
              </div>



              {/* 비밀번호 */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    비밀번호 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="비밀번호"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    비밀번호 확인 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="비밀번호 확인"
                    required
                  />
                </div>
              </div>

              {/* 버튼: 다음 단계 */}
              <button
                type="button"
                onClick={handleNextStep}
                className="w-full mt-4 py-2.5 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                다음 단계로
              </button>
            </>
          )}

          {/* ================= STEP 2 ================= */}
          {step === 2 && (
            <>
              {/* 이름/핸드폰(확인용, 수정불가) */}
              <div className="p-3 bg-gray-50 rounded mb-4 text-xs text-gray-500">
                <span className="font-semibold">{name}</span>님, 마지막 인증 정보를 입력해 주세요.
              </div>

              {/* 약국명 (약국만) */}
              {role === 'pharmacy' && (
                <div>
                  <label className="block text-sm font-medium mb-1">약국명 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={pharmacyName}
                    onChange={(e) => setPharmacyName(e.target.value)}
                    placeholder="약국명을 입력해 주세요."
                    required
                  />
                </div>
              )}

              {/* 면허번호 */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  약사 면허번호 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  placeholder={role === 'pharmacy' ? '약국 담당 약사 면허번호' : '약사 면허번호'}
                  required
                />
              </div>

              {/* 면허증 파일 */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  면허증 사본 <span className="text-red-500">*</span> (필수 업로드)
                </label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={handleLicenseFileChange}
                  className="w-full text-sm"
                />
                <p className="mt-1 text-xs text-gray-400">
                  JPG, PNG 또는 PDF. 관리자 승인 시 면허 확인용으로 사용됩니다.
                </p>
              </div>

              {/* 주소 */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  {role === 'pharmacy' ? '약국 주소' : '주소'} <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    className="w-24 border rounded-lg px-3 py-2 text-sm bg-gray-50"
                    value={postcode}
                    readOnly
                    placeholder="우편번호"
                  />
                  <button
                    type="button"
                    onClick={handleSearchAddress}
                    disabled={isSubmitting}
                    className="flex-1 border rounded-lg px-3 py-2 text-sm font-semibold text-blue-600 border-blue-500 hover:bg-blue-50 disabled:opacity-60"
                  >
                    주소 검색
                  </button>
                </div>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 text-sm mb-2 bg-gray-50"
                  value={address}
                  readOnly
                  placeholder="주소를 검색해 주세요."
                />
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={detailAddress}
                  onChange={(e) => setDetailAddress(e.target.value)}
                  placeholder="상세 주소를 입력해 주세요."
                  required
                />
              </div>

              {/* 버튼들 */}
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 py-2.5 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  이전으로
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-[2] py-2.5 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {isSubmitting ? '가입 중...' : '회원 가입 신청'}
                </button>
              </div>
            </>
          )}

          {errorMessage && (
            <p className="text-sm text-red-500 mt-1 whitespace-pre-line text-center">
              {errorMessage}
            </p>
          )}
        </form>
      </div>
    </div>
  )
}
